import {
    type FC,
    useId,
    memo
} from "react";
import {
    View
} from "react-native";
import type IColorAreaPlaneProps from "./type";
import {
    NCoreUIKitTheme
} from "../../../../core/hooks";
import Svg, {
    LinearGradient,
    ClipPath,
    Polyline,
    Polygon,
    Pattern,
    Stop,
    Rect,
    Defs,
    G
} from "react-native-svg";
import {
    getColorAreaStripHeight,
    COLOR_AREA_HALO_ALPHA
} from "../../util";
import {
    getMirrorStyle
} from "../../../../helpers/direction";
import {
    withAlpha
} from "../../../../utils/color";
import stylesheet from "../../stylesheet";

const ColorAreaPlane: FC<IColorAreaPlaneProps> = ({
    isGamutShown,
    customTheme,
    direction,
    guides,
    height,
    strips,
    width
}) => {
    const {
        borders,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext(customTheme);

    const idPrefix = useId().replace(/[^a-zA-Z0-9]/g, "");

    if(width <= 0 || height <= 0) {
        return null;
    }

    const renderRows = () => {
        if(!strips) {
            return null;
        }

        return strips.rows.map((row, index) => <Rect
            fill={`url(#${idPrefix}-row-${index})`}
            height={getColorAreaStripHeight()}
            key={`row-${index}`}
            width={row.width}
            y={row.y}
            x={0}
        />);
    };

    const renderGradients = () => {
        if(!strips) {
            return null;
        }

        return strips.rows.map((row, index) => <LinearGradient
            id={`${idPrefix}-row-${index}`}
            key={`gradient-${index}`}
            x1="0"
            x2="1"
            y1="0"
            y2="0"
        >
            {row.stops.map((stop) => <Stop
                stopColor={stop.color}
                offset={stop.offset}
                key={stop.offset}
            />)}
        </LinearGradient>);
    };

    const renderGuideLines = () => {
        const haloColor = withAlpha({
            color: colors.content.container.default,
            alpha: COLOR_AREA_HALO_ALPHA
        });

        return guides.map((guide, index) => {
            const isPrimary = index === 0;

            return <G
                key={`guide-${guide.target}`}
            >
                <Polyline
                    strokeWidth={borders.line * 3}
                    strokeLinejoin="round"
                    points={guide.points}
                    strokeLinecap="round"
                    stroke={haloColor}
                    fill="none"
                />
                <Polyline
                    strokeWidth={isPrimary ? borders.line : borders.line * 1.5}
                    strokeDasharray={isPrimary ? [
                        spaces.spacingXs,
                        spaces.spacingXs - borders.line
                    ].join(" ") : `0.01 ${spaces.spacingXs - borders.line}`}
                    strokeLinecap={isPrimary ? "butt" : "round"}
                    stroke={colors.content.container.inverse}
                    strokeLinejoin="round"
                    points={guide.points}
                    fill="none"
                />
            </G>;
        });
    };

    return <View
        style={[
            stylesheet.layer,
            getMirrorStyle({
                direction
            })
        ]}
        aria-hidden={true}
    >
        <Svg
            height={height}
            width={width}
        >
            <Defs>
                <Pattern
                    patternTransform="rotate(45)"
                    patternUnits="userSpaceOnUse"
                    height={spaces.spacingXs}
                    id={`${idPrefix}-hatch`}
                    width={spaces.spacingXs}
                >
                    <Rect
                        fill={colors.system.state.overlay.hover.default}
                        height={spaces.spacingXs}
                        width={borders.line}
                    />
                </Pattern>
                {strips && <ClipPath
                    id={`${idPrefix}-clip`}
                >
                    <Polygon
                        points={strips.polygon}
                    />
                </ClipPath>}
                {renderGradients()}
            </Defs>
            <Rect
                fill={colors.content.container.default}
                height={height}
                width={width}
            />
            {isGamutShown && <Rect
                fill={`url(#${idPrefix}-hatch)`}
                height={height}
                width={width}
            />}
            <G
                clipPath={`url(#${idPrefix}-clip)`}
            >
                {renderRows()}
            </G>
            {isGamutShown && strips && <Polyline
                stroke={colors.content.border.default}
                strokeWidth={borders.line}
                points={strips.boundary}
                strokeLinejoin="round"
                fill="none"
            />}
            {renderGuideLines()}
        </Svg>
    </View>;
};
export default memo(ColorAreaPlane);
